import {
  BusinessInfoDoc,
  CallCreateUserRequest,
  CallCreateUserResponse,
  CallFindEmailRequest,
  CallFindEmailResponse,
  CallSendOrderHistoryEmailRequest,
  CallSendOrderHistoryEmailResponse,
  CallSendVerificationEmailRequest,
  CallSendVerificationEmailResponse,
  CommerceConf,
  CustomPromotionSectionDoc,
  DeleteAccountRequest,
  DeviceDoc,
  InvoiceDetailDoc,
  InvoiceDoc,
  IssueTaskDoc,
  Log,
  LogCommerce,
  LogDoc,
  LogOrder,
  NotificationDoc,
  OrderDoc,
  OrderStatusCode,
  ProductDoc,
  ProductGroupForCategorySubsetDoc,
  ProductGroupForTagDoc,
  ProductStateCode,
  PromotionSectionDoc,
  Store,
  StoreDoc,
  StoreIssueDoc,
  UserDoc,
} from '@gooduncles/gu-app-schema';
import { Transaction, arrayRemove, arrayUnion } from 'firebase/firestore';
import { groupBy } from 'lodash-es';
import { map } from 'rxjs';
import { GaEvent } from 'src/schema/schema-ga-event';
import { formatDate } from 'src/utils/date-util';
import { FirebaseManager, QueryOption, WHERE } from 'src/utils/firebase/firebase-manager';

/**
 * redux & ga에서 사용불가한
 * Timestamp를 제거한다.
 */
export const deleteTimestamp = (data: any) => {
  delete data._timeUpdate;
  delete data._timeCreate;
  delete data._timeDelete;
  return data;
};

const fb = FirebaseManager.getInstance();

// Transaction
export const firebaseTransaction = <T>(updateFunction: (transaction: Transaction) => Promise<T>) =>
  fb.transaction(updateFunction);

/** 사용자 생성 */
export const callCreateUser = fb.getCallable<CallCreateUserRequest, CallCreateUserResponse>('callCreateUser');
/** 이메일 찾기 */

export const callFindEmail = fb.getCallable<CallFindEmailRequest, CallFindEmailResponse>('callFindEmail');
/** 주문 내역 엑셀 이메일 발송 */
export const callSendOrderHistoryExcelEmail = fb.getCallable<
  CallSendOrderHistoryEmailRequest,
  CallSendOrderHistoryEmailResponse
>('callSendOrderHistoryExcelEmail');
/** 매장 이메일 변경을 위한 인증 메일 발송 */
export const callSendVerificationEmail = fb.getCallable<
  CallSendVerificationEmailRequest,
  CallSendVerificationEmailResponse
>('callSendVerificationEmail');

// Auth
export const observeFriebaseAuth = () => fb.observeAuthState();
export const sendPasswordResetEmail = (email: string) => fb.sendPasswordResetEmail(email);
export const login = (email: string, password: string) => fb.signIn(email, password);
export const logout = () => fb.signOut();

// GA Event
export const sendGaEvent = (event: GaEvent) => fb.gaLogEvent(event.name, event.eventParams);
export const setUserForGa = (uid: string) => fb.setGaUserId(uid);
export const setUserPropertiesForGa = (userProperties: any) => fb.setGaUserProperties(userProperties);

// LogCommerce
const logCommercePath = 'logCommerce';
export const createLogCommerce = (data: Log<LogCommerce>) => fb.createDoc(logCommercePath, undefined, data);

// LogOrder
const logOrderPath = 'logOrder';
export const getLogOrders = (wheres: WHERE[], options?: QueryOption) =>
  fb.getDocsArrayWithWhere<LogDoc<LogOrder>>(logOrderPath, wheres, options);

// User
const userPath = 'user';
export const observeUserDoc = (uid: string) => fb.observeDoc<UserDoc>(`${userPath}/${uid}`);
export const getUserDoc = async (uid: string) => fb.getDoc<UserDoc>(`${userPath}/${uid}`);
export const updateUser = (uid: string, data: Partial<UserDoc>) => fb.updateDoc(userPath, uid, data);
export const addFavoriteProduct = (uid: string, productId: ProductDoc['_id']) =>
  fb.updateDoc(userPath, uid, {
    favoriteProducts: arrayUnion(productId),
  });
export const removeFavoriteProduct = (uid: string, productId: ProductDoc['_id']) =>
  fb.updateDoc(userPath, uid, {
    favoriteProducts: arrayRemove(productId),
  });

// device
const devicePath = 'device';
export const observeDeviceDoc = (deviceId: string) => fb.observeDoc<DeviceDoc>(`${devicePath}/${deviceId}`);
export const updateDevice = (deviceId: DeviceDoc['_id'], data: Partial<DeviceDoc>) =>
  fb.updateDoc(devicePath, deviceId, data);
export const setDevice = (deviceId: DeviceDoc['_id'], data: Partial<DeviceDoc>) =>
  fb.setDoc(devicePath, deviceId, data, { bMerge: true });

// Store
const storePath = 'store';
export const observeStoreDoc = (storeId: string) => fb.observeDoc<StoreDoc>(`${storePath}/${storeId}`);
export const getStoreDoc = async (storeId: string) => fb.getDoc<StoreDoc>(`${storePath}/${storeId}`);
export const updateStore = async (storeId: string, data: Partial<Store>) => fb.updateDoc(storePath, storeId, data);

// Config
const commerceConfPath = 'config/commerce';
export const getCommerceConfDocRef = () => fb.getDocRef('config', 'commerce');
export const getCommerceConf = (fromServer = false) => fb.getDoc<CommerceConf>(commerceConfPath, fromServer);
export const observeCommerceConf = () => fb.observeDoc<CommerceConf>(commerceConfPath);
const businessInfoConfPath = 'config/businessInfo';
/** 굿엉클스의 사업자정보 */
export const getBusinessInfoConf = (fromServer = false) => fb.getDoc<BusinessInfoDoc>(businessInfoConfPath, fromServer);

// Notification
const notificationPath = 'notification';
export const getNotifications = (wheres: WHERE[], options?: QueryOption) =>
  fb.getDocsArrayWithWhere<NotificationDoc>(notificationPath, wheres, options);
export const getNotificationDoc = (notificationId: string) =>
  fb.getDoc<NotificationDoc>(`${notificationPath}/${notificationId}`);
export const updateNotificationDoc = (notificationId: string, updateData: Partial<NotificationDoc>) =>
  fb.updateDoc(notificationPath, notificationId, updateData);

// Order
const orderPath = 'order';
export const getOrderDocRef = (orderId?: string) => fb.getDocRef(orderPath, orderId);
export const getOrder = async (orderId: string) => fb.getDoc<OrderDoc>(`${orderPath}/${orderId}`);
export const getOrders = (where: WHERE[], options?: QueryOption) =>
  fb.getDocsArrayWithWhere<OrderDoc>(orderPath, where, options);
export const getOrderRecords = (where: WHERE[], options?: QueryOption) =>
  fb.getDocsWithWhere<OrderDoc>(orderPath, where, options);
export const updateOrder = (orderId: string, updateData: Partial<OrderDoc>) =>
  fb.updateDoc(orderPath, orderId, updateData);
export const cancelOrder = (orderId: string) =>
  fb.updateDoc(orderPath, orderId, { orderStatus: OrderStatusCode.CANCELED });
/** 완료된 주문(수락, 배송완료)이 있는지 확인한다. */
export const getOrderedOrderCountForStore = async (storeId: string) => {
  const where: WHERE[] = [
    ['storeId', '==', storeId],
    ['orderStatus', 'in', [OrderStatusCode.DELIVERED, OrderStatusCode.ACCEPTED]],
  ];
  return fb.getCollectionCount(orderPath, where);
};
export const getSubmittedOrderCountForStore = async (storeId: string) => {
  const where: WHERE[] = [
    ['storeId', '==', storeId],
    ['orderStatus', '==', OrderStatusCode.SUBMITTED],
  ];
  return fb.getCollectionCount(orderPath, where);
};
export const orderSubscriptionForStore = (storeId: string, monthRange = 6) => {
  const now = new Date();
  const endDate = formatDate(new Date(now.setMonth(now.getMonth() - monthRange)), "yyyy-MM-dd'T'HH:mm:ss+0900");
  const where: WHERE[] = [
    ['storeId', '==', storeId],
    ['orderDate', '>=', endDate],
  ];
  return fb
    .observeCollection<OrderDoc>(orderPath, where, {
      sortKey: 'orderDate',
      orderBy: 'desc',
    })
    .pipe(map((orders) => groupBy(orders, 'orderStatus')));
};
/** 진행중(SUBMITTED) 주문만 구독한다. 전역 로딩 게이트용 — 작고 빠르다. */
export const submittedOrderSubscriptionForStore = (storeId: string) => {
  const where: WHERE[] = [
    ['storeId', '==', storeId],
    ['orderStatus', '==', OrderStatusCode.SUBMITTED],
  ];
  return fb.observeCollection<OrderDoc>(orderPath, where, {
    sortKey: 'orderDate',
    orderBy: 'desc',
  });
};

// StoreIssue
const storeIssuePath = 'storeIssue';
export const getStoreIssuesForStoreWithWhere = (wheres: WHERE[] = [], options?: QueryOption) =>
  fb.getDocsArrayWithWhere<StoreIssueDoc>(storeIssuePath, wheres, options);

export const getStoreIssue = (docId: string) => fb.getDoc<StoreIssueDoc>(`${storeIssuePath}/${docId}`);

// IssueTask
const issueTaskPath = 'issueTask';
export const getIssueTask = (docId: string) => fb.getDoc<IssueTaskDoc>(`${issueTaskPath}/${docId}`);
export const getIssueTasks = (wheres: WHERE[] = [], options?: QueryOption) =>
  fb.getDocsArrayWithWhere<IssueTaskDoc>(issueTaskPath, wheres, options);

// productGroupForCategorySubset
const productGroupForCategorySubsetPath = 'productGroupForCategorySubset';
export const getProductGroupForCategorySubset = async (category: string) =>
  fb.getDoc<ProductGroupForCategorySubsetDoc>(`${productGroupForCategorySubsetPath}/${category}`);
export const observeProductGroupForCategorySubset = () =>
  fb.observeCollection<ProductGroupForCategorySubsetDoc>(productGroupForCategorySubsetPath, []);

// productGroupForTag
const productGroupForTagPath = 'productGroupForTag';
export const getProductGroupForTag = async (tag: string) =>
  fb.getDoc<ProductGroupForTagDoc>(`${productGroupForTagPath}/${tag}`);

// promotionSection
const promotionSectionPath = 'promotionSection';
export const observePromotionSection = (where: WHERE[] = [], options?: QueryOption) =>
  fb.observeCollection<PromotionSectionDoc>(promotionSectionPath, where, options);

// invoice
const invoicePath = 'invoice';
export const getInvoice = async (invoiceId: string) => fb.getDoc<InvoiceDoc>(`${invoicePath}/${invoiceId}`);

// invoiceDetail
const invoiceDetailPath = 'invoiceDetail';
export const getInvoiceDetail = async (invoiceId: string) =>
  fb.getDoc<InvoiceDetailDoc>(`${invoiceDetailPath}/${invoiceId}`);

// deleteAccountRequest
const deleteAccountRequestPath = 'deleteAccountRequest';
export const getDeleteAccountRequest = async (id: string) =>
  fb.getDoc<DeleteAccountRequest>(`${deleteAccountRequestPath}/${id}`);
export const createDeleteAccountRequest = async (data: DeleteAccountRequest) =>
  fb.createDoc(deleteAccountRequestPath, data._id, data);

// customPromotionSection
const customPromotionSectionPath = 'customPromotionSection';
export const observeCustomPromotionSectionFor = (storeId: string) =>
  fb.observeCollection<CustomPromotionSectionDoc>(customPromotionSectionPath, [['storeId', '==', storeId]]);

// product
const productPath = 'product';
export const getProductDocRef = (productId: ProductDoc['_id']) => fb.getDocRef(productPath, productId);
export const getProductDoc = async (productId: ProductDoc['_id']) =>
  fb.getDoc<ProductDoc>(`${productPath}/${productId}`);
/**
 * 특정 카테고리의 유효한 상품 개수를 가져온다.(재고가 없는 상품 포함)
 * @param category
 */
export const getProductCountForCategory = async (category: string) => {
  const where: WHERE[] = [
    ['categories', 'array-contains', category],
    ['state', 'in', [ProductStateCode.STOCK, ProductStateCode.OUTOFSTOCK]],
  ];
  return fb.getCollectionCount(productPath, where);
};
export const getProducts = (wheres: WHERE[], options?: QueryOption) =>
  fb.getDocsArrayWithWhere<ProductDoc>(productPath, wheres, options);
